// src/services/api.js
import axios from 'axios';

// ✅ Now process.env will be available thanks to dotenv-webpack
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.8664.wiki';

console.log('API Base URL:', API_BASE_URL); // Debug log


const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

// Request interceptor to attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle unauthorized errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
    }
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;
    
    // Handle token expired case
    if (error.response?.status === 403 && 
        error.response?.data?.error?.includes('token') &&
        !originalRequest._retry) {
      
      // Remove expired token
      localStorage.removeItem('token');
      
      // Show message and redirect after delay
      setTimeout(() => {
        alert('Your session has expired. Please log in again.');
        window.location.href = '/login';
      }, 1000);
      
      return Promise.reject({
        ...error,
        message: 'Session expired - please refresh and log in again'
      });
    }
    
    // Other error cases
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
    }
    
    return Promise.reject(error);
  }
);

// Auth
export const verifyToken = async () => {
  try {
    const response = await api.get('/auth/verify');
    return { success: true, data: response.data.user };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
// Auth
export const login = async (email, password) => {
  try {
    const response = await api.post('/auth/login', { email, password });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Login failed'
    };
  }
};

export const logoutAPI = async () => {
  try {
    const token = localStorage.getItem('token');

    const response = await api.post(
      '/auth/logout',
      {}, // POST body (empty)
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Logout failed'
    };
  }
};

export const changePassword = async (userId, oldPassword, newPassword) => {
  try {
    const response = await api.post('/auth/change-password', { 
      userId, 
      oldPassword, 
      newPassword 
    });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to change password'
    };
  }
}

export const requestPasswordReset = async (email) => {
  try {
    const response = await api.post('/auth/request-password-reset', { email });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to request password reset'
    };
  }
};

// src/services/api.js
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to reset password'
    };
  }
};

// export const verifyToken = async () => {
//   try {
//     const response = await api.get('/auth/verify');
//     return { success: true, data: response.data };
//   } catch (error) {
//     return {
//       success: false,
//       error: error.response?.data?.message || 'Token verification failed'
//     };
//   }
// };

// Audits
export const submitAudit = async (auditData) => {
  const response = await api.post('/audits', auditData);
  return response.data;
};

// export const getLandingAudits = async (searchTerm = '') => {
//   const response = await api.get('/audits/landing', {
//     params: { search: searchTerm },
//   });
//   return response.data;
// };
// services/api.js
export const getLandingAudits = async (params = {}) => {
  const response = await api.get('/audits/landing', { params });
  return response.data; // { items: [...], totalCount: 123 }
};
// Delete single audit item
export const deleteAudit = async (id) => {
  const response = await api.delete(`/audits/item/${id}`);
  return response.data;
};

// Delete multiple audit items (batch delete)
export const deleteMultipleAuditItems = async (itemIds) => {
  const response = await api.delete('/audits/items/batch', { 
    data: { itemIds } 
  });
  return response.data;
};

// Delete audit items by status
export const deleteAuditItemsByStatus = async (status) => {
  const response = await api.delete(`/audits/status/${status}`);
  return response.data;
};
// Add this to your existing api.js exports
export const uploadAuditLandingCSV = (formData) => {
  return api.post('/audits/landing/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Export combined CSV API
export const exportCombinedCSV = async (params) => {
  const response = await api.get('/audits/export/combined', {
    params,
    responseType: 'blob' // Important! So the browser handles CSV as file
  });
  return response;
};

// Manual Search page export - supports searching by country OR by
// latitude/longitude + radius (see backend/controllers/auditController.js
// generateManualSearchExcel)
export const exportManualSearchXLSX = async (params) => {
  const response = await api.get('/audits/export/manual-search', {
    params,
    responseType: 'blob'
  });
  return response;
};

export const getFinalAudits = async (params = {}) => {
  const response = await api.get('/audits/final', { params });
  return response.data; // { items: [...], totalCount: 123 }
};

export const getAuditLogs = async (params = {}) => {
  const response = await api.get('/audits/logs', { params });
  return response.data; // { items: [...], totalCount: 123 }
};
// export const getFinalAudits = async (searchTerm = '') => {
//   const response = await api.get('/audits/final', {
//     params: { search: searchTerm },
//   });
//   return response.data;
// };

export const processWithQwen = async (auditItemId) => {
  const response = await api.post(`/audits/${auditItemId}/process`);
  return response.data;
};

// Admin - Users
export const getUsers = async () => {
  const response = await api.get('/admin/users');
  return response.data;
};

export const getAllLoginHistory = async () => {
  const response = await api.get('/admin/loginhistories');
  return response.data;
};

export const deleteUser = async (id) => {
  const response = await api.delete(`/admin/users/${id}`);
  return response.data;
};

export const getUserById = async (id) => {
  const response = await api.get(`/admin/users/${id}`);
  return response.data;
};

export const updateUser = async (id, userData) => {
  const response = await api.put(`/admin/users/${id}`, userData);
  return response.data;
};

export const addUser = async (userData) => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

// Countries
// export const getCountries = async () => {
//   const response = await api.get('/countries');
//   return response.data;
// };

export const getCountries = async (params = {}) => {
  const response = await api.get('/countries', { params });
  return response.data; // { items: [...], totalCount: 123 }
};

export const addCountry = async (countryData) => {
  const response = await api.post('/countries', countryData);
  return response.data;
};

// In your api.js, add the upload flag endpoint
export const uploadFlag = async (formData) => {
  const response= api.post('/upload-flag', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response;
};

// In services/api.js
export const deleteCountry = async (id) => {
  const response = await api.delete(`/countries/${id}`);
  return response.data;
};

// Audit Items API
// export const getAuditItems = async () => {
//   const response = await api.get('/audit-items');
//   return response.data;
// };

export const getAuditItems = async (params = {}) => {
  const response = await api.get('/audit-items', { params });
  return response.data; // { items: [...], totalCount: 123 }
};

export const addAuditItem = async (data) => {
  const response = await api.post('/audit-items', data);
  return response.data;
};

export const updateAuditItem = async (id, data) => {
  const response = await api.put(`/audit-items/${id}`, data);
  return response.data;
};

export const deleteAuditItem = async (id) => {
  const response = await api.delete(`/audit-items/${id}`);
  return response.data;
};

// export const getAuditLogs = async (params = {}) => {
//   const queryString = new URLSearchParams(params).toString();
//   const response = await api.get(`/audits/logs?${queryString}`);
//   return response.data;
// };

// Barcode Library API
// export const getBarcodeLibraries = async () => {
//   const response = await api.get('/barcodes/Library');
//   return response.data;
// };

export const getBarcodeLibraries = async (params = {}) => {
  const response = await api.get('/barcodes/Library', { params });
  return response.data; // { items: [...], totalCount: 123 }
};

export const getBarcodeLibraryById = async (id) => {
  const response = await api.get(`/barcodes/Library/${id}`);
  return response.data;
};

export const getBarcodeLibraryByCode = async (barcode) => {
  const response = await api.get(`/barcodes/Library/code/${barcode}`);
  return response.data;
};

export const addBarcodeLibrary = async (data) => {
  const response = await api.post('/barcodes/Library', data);
  return response.data;
};

export const updateBarcodeLibrary = async (id, data) => {
  const response = await api.put(`/barcodes/Library/${id}`, data);
  return response.data;
};

export const deleteBarcodeLibrary = async (id) => {
  const response = await api.delete(`/barcodes/Library/${id}`);
  return response.data;
};

// Add this to your existing api.js exports
export const uploadBarcodeLibraryCSV = (formData) => {
  return api.post('/barcodes/Library/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// ✅ BARCODE ITEMS API

// Submit single barcode item
export const submitBarcodeItem = async (data) => {
  const response = await api.post('/barcodes', data);
  return response.data;
};

// Upload CSV for barcode items
export const uploadBarcodeItemsCSV = (formData) => {
  return api.post('/barcodes/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

// Get landing barcode items
// export const getLandingBarcodeItems = async () => {
//   const response = await api.get('/barcodes/landing');
//   return response.data;
// };

// services/api.js
export const getLandingBarcodeItems = async (params = {}) => {
  const response = await api.get('/barcodes/landing', { params });
  return response.data; // { items: [...], totalCount: 123 }
};

// services/api.js
export const getFinalBarcodeItems = async (params = {}) => {
  const response = await api.get('/barcodes/final', { params });
  return response.data; // { items: [...], totalCount: 123 }
};

export const getUnknownBarcodeItems = async (params = {}) => {
  const response = await api.get('/barcodes/unknown', { params });
  return response.data; // { items: [...], totalCount: 123 }
};
// // Get final barcode items
// export const getFinalBarcodeItems = async () => {
//   const response = await api.get('/barcodes/final');
//   return response.data;
// };

// // Get unknown barcode items
// export const getUnknownBarcodeItems = async () => {
//   const response = await api.get('/barcodes/unknown');
//   return response.data;
// };

// Process single barcode item (call your AI + barcode lookup)
export const processBarcodeItem = async (barcodeItemId) => {
  const response = await api.post(`/barcodes/${barcodeItemId}/process`);
  return response.data;
};

// Delete barcode item
export const deleteBarcodeItem = async (id) => {
  const response = await api.delete(`/barcodes/${id}`);
  return response.data;
};

export const updateBarcodeCompany = async (id, data) => {
  const response = await api.post(`/barcodes/${id}/update-company`, data);
  return response.data;
};

export const approveBarcodeItem = async (id) => {
  const response = await api.post(`/barcodes/${id}/approve`);
  return response.data;
};

// Companies API (for autocomplete)

export const searchCompanies = async (query) => {
  const response = await api.get('/companies/search', { params: { q: query } });
  return response.data;
};

// services/api.js
export const getCompanies = async (params = {}) => {
  const response = await api.get('/companies', { params });
  return response.data;
};

export const createCompany = async (data) => {
  const response = await api.post('/companies', data);
  return response.data;
};

export const updateCompany = async (id, data) => {
  const response = await api.put(`/companies/${id}`, data);
  return response.data;
};

export const deleteCompany = async (id) => {
  const response = await api.delete(`/companies/${id}`);
  return response.data;
};

// Image API functions
export const getImages = async (params = {}) => {
  const response = await api.get('/images', { params });
  return response.data;
};

export const getImagesByStatus = async (status, params = {}) => {
  const response = await api.get(`/images/status/${status}`, { params });
  return response.data;
};

export const uploadImage = async (formData) => {
  return api.post('/images', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const analyzeImage = async (id) => {
  const response = await api.post(`/images/${id}/analyze`);
  return response.data;
};

export const confirmImage = async (id) => {
  const response = await api.put(`/images/${id}/confirm`);
  return response.data;
};

export const getImage = async (id) => {
  const response = await api.get(`/images/${id}`);
  return response.data;
};

export const deleteImage = async (id) => {
  const response = await api.delete(`/images/${id}`);
  return response.data;
};

export const updateUnknownImage = async (id, data) => {
  const response = await api.put(`/images/unknown/${id}`, data);
  return response.data;
};

// Add to api.js
export const deleteMultipleImages = async (imageIds) => {
  const response = await api.delete('/images/batch', { 
    data: { imageIds } 
  });
  return response.data;
};

// src/services/api.js (add these)
// export const uploadTrainingImage = async (formData) => {
//   return api.post('/ai-training/upload', formData, {
//     headers: {
//       'Content-Type': 'multipart/form-data'
//     }
//   });
// };

// export const processTrainingImage = async (id) => {
//   const response = await api.post(`/ai-training/process/${id}`);
//   return response.data;
// };

// export const getTrainingImages = async () => {
//   const response = await api.get('/ai-training/images');
//   return response.data;
// };

// export const agreeWithDetection = async (id) => {
//   const response = await api.post(`/ai-training/agree/${id}`);
//   return response.data;
// };

// export const disagreeWithDetection = async (id, correction) => {
//   const response = await api.post(`/ai-training/disagree/${id}`, correction);
//   return response.data;
// };

// export const getAllAuditItems = async () => {
//   const response = await api.get('/ai-training/audit-items');
//   return response.data;
// };
// In your api service file
export const uploadTrainingImage = async (formData) => {
  return api.post('/ai-training/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const processTrainingImage = async (id) => {
  const response = await api.post(`/ai-training/process/${id}`);
  return response.data;
};

export const getTrainingImages = async () => {
  const response = await api.get('/ai-training/images');
  return response.data;
};

export const agreeWithDetection = async (id) => {
  const response = await api.post(`/ai-training/agree/${id}`);
  return response.data;
};

// export const disagreeWithDetection = async (id, correction) => {
//   const response = await api.post(`/ai-training/disagree/${id}`, {
//     detected_label: correction.detected_label,
//     item_number: correction.item_number,
//     description: correction.description
//   });
//   return response.data;
// };
// In api.js, update disagreeWithDetection:

export const disagreeWithDetection = async (id, correction) => {
  const response = await api.post(`/ai-training/disagree/${id}`, {
    detected_label: correction.detected_label,
    item_number: correction.item_number,
    description: correction.description,
    grid_x: correction.grid_x,
    grid_y: correction.grid_y
  });
  return response;
};
export const getAllAuditItems = async () => {
  const response = await api.get('/ai-training/audit-items');
  return response.data;
};

// Add this to your api.js
export const getImageItemsForTraining = async (originalImageId) => {
  const response = await api.get(`/ai-training/image-items/${originalImageId}`);
  return response.data;
};

// Search Logs API functions
export const getSearchLogs = async (params = {}) => {
  const response = await api.get('/email/search-logs', { params });
  return response.data; // { data: [...], pagination: {...} }
};

export const getSearchLogDetails = async (id) => {
  const response = await api.get(`/email/search-logs/${id}`);
  return response.data; // { success: true, data: {...} }
};

export const exportSearchLogsCSV = async (params) => {
  const response = await api.get('/email/search-logs/export', {
    params,
    responseType: 'blob'
  });
  return response;
};

// Get current AI prompt
// export const getAIPrompt = async () => {
//   const response = await api.get('/ai-prompt');
//   return response.data;
// };

// // Update AI prompt
// export const updateAIPrompt = async (prompt) => {
//   const response = await api.put('/ai-prompt', { prompt });
//   return response.data;
// };
// Get current AI prompt by type
export const getAIPrompt = async (type = 'landing') => {
  const response = await api.get('/ai-prompt', { params: { type } });
  return response.data;
};

// Update AI prompt by type
export const updateAIPrompt = async (prompt, type = 'landing') => {
  const response = await api.put('/ai-prompt', { prompt, type });
  return response.data;
};

// Reset AI prompt by type
export const resetAIPrompt = async (type = 'landing') => {
  const response = await api.post('/ai-prompt/reset', { type });
  return response.data;
};

// Add to api.js
export const stopAutoProcessForImage = async (id) => {
  const response = await api.put(`/images/${id}/stop-auto-process`);
  return response.data;
};

export const resumeAutoProcessForImage = async (id) => {
  const response = await api.put(`/images/${id}/resume-auto-process`);
  return response.data;
};

// Stop auto-process for a specific audit item
export const stopAutoProcessForAudit = async (auditItemId) => {
  const response = await api.put(`/audits/item/${auditItemId}/stop-auto-process`);
  return response.data;
};

// Resume auto-process for a specific audit item
export const resumeAutoProcessForAudit = async (auditItemId) => {
  const response = await api.put(`/audits/item/${auditItemId}/resume-auto-process`);
  return response.data;
};

// Get all stopped audit items from database
export const getStoppedAuditItems = async () => {
  const response = await api.get('/audits/stopped-items');
  return response.data;
};

// Sync frontend held items with database
export const syncAuditHeldItems = async (heldItemIds) => {
  const response = await api.post('/audits/sync-held-items', { heldItemIds });
  return response.data;
};

// Track when an audit item is processed
export const trackAuditProcessing = async (auditItemId) => {
  const response = await api.post(`/audits/item/${auditItemId}/track-processing`);
  return response.data;
};

export const deleteTrainingItems = async (imageId, gridPositions) => {
  const response = await api.delete(`/ai-training/${imageId}/items`, {
    data: { grid_positions: gridPositions }
  });
  return response.data;
};